import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useContactInfo } from '../../hooks/useContactInfo';
import { whatsappUrl } from '../../utils/whatsapp';
import WhatsAppIcon from './WhatsAppIcon';

const FloatingWhatsApp: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const { contactInfo } = useContactInfo();

  useEffect(() => {
    // Show the button after a short delay
    const timer = setTimeout(() => {
      setIsVisible(true);
      // Show tooltip after button appears
      setTimeout(() => {
        setIsTooltipVisible(true);
        // Hide tooltip after 5 seconds
        setTimeout(() => {
          setIsTooltipVisible(false);
        }, 5000);
      }, 1000);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      'Hello! I\'m interested in learning more about NWLC courses and programs.'
    );
    
    const url = whatsappUrl(contactInfo.whatsapp, decodeURIComponent(message));
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (!isVisible || !contactInfo.whatsapp) return null;

  return (
    <>
      {/* Floating WhatsApp Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <div className="relative group">
          {/* Tooltip/Chat Bubble */}
          <div
            className={`absolute bottom-20 right-0 bg-white rounded-lg shadow-xl p-4 w-64 transition-all duration-300 ${
              isTooltipVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
            }`}
            style={{
              animation: isTooltipVisible ? 'bounceFloat 2s ease-in-out infinite' : 'none'
            }}
          >
            <button
              onClick={() => setIsTooltipVisible(false)}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close tooltip"
            >
              <X size={16} />
            </button>
            <p className="text-sm text-gray-700 pr-6 font-medium">
              👋 Need help? Chat with us on WhatsApp!
            </p>
            <p className="text-xs text-gray-500 mt-1">
              We typically reply within minutes
            </p>
            {/* Arrow pointing to button */}
            <div className="absolute bottom-0 right-6 transform translate-y-1/2 rotate-45 w-4 h-4 bg-white border-r border-b border-gray-200"></div>
          </div>

          {/* WhatsApp Button with proper logo - Smaller size */}
          <button
            onClick={handleWhatsAppClick}
            onMouseEnter={() => setIsTooltipVisible(true)}
            onMouseLeave={() => setIsTooltipVisible(false)}
            className="relative bg-green-500 hover:bg-green-600 text-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 group"
            aria-label="Chat on WhatsApp"
          >
            {/* Pulse animation ring */}
            <div className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-25"></div>
            
            {/* WhatsApp SVG Icon - Smaller */}
            <WhatsAppIcon className="w-5 h-5 relative z-10" />

            {/* Notification dot - Smaller */}
            <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white"></span>
          </button>
        </div>
      </div>

      <style>{`
        @keyframes bounceFloat {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @media (max-width: 640px) {
          .fixed.bottom-6.right-6 {
            bottom: 20px;
            right: 20px;
          }
          
          /* Even smaller on mobile */
          button[aria-label="Chat on WhatsApp"] {
            padding: 10px;
          }
          
          button[aria-label="Chat on WhatsApp"] svg {
            width: 18px;
            height: 18px;
          }
        }

        /* Improve visibility on all backgrounds */
        button[aria-label="Chat on WhatsApp"] {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        button[aria-label="Chat on WhatsApp"]:hover {
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </>
  );
};

export default FloatingWhatsApp;
